import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Select, { MultiValue } from 'react-select';
import { useAuth } from '../../../firebase/AuthProvider';
import { User } from '../../../interfaces/User';
import Sprint from '../../../interfaces/sprint/Sprint';
import TicketStatus from '../../../interfaces/ticket/TicketStatus';
import Tag from '../../../interfaces/tag/Tag';
import { NewTicketRequest } from '../../../interfaces/ticket/NewTicketRequest';
import { createTicket, submitBug } from '../../../services/ticketServices/TicketService';
import RichTextEditor from '../../ui/RichTextEditor/RichTextEditor';
import { SubmitBugRequest } from '../../../interfaces/ticket/SubmitBugRequest';
import { getSortedStatuses } from '../../../utilities/statusUtils';

interface OptionType {
    value: string;
    label: string;
}

interface CreateTicketModalProps {
    show: boolean;
    onHide: () => void;
    assignees: User[];
    sprints: Sprint[];
    statuses: TicketStatus[];
    tags: Tag[];
    onTicketCreated: () => void;
}

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({ show, onHide, assignees, sprints, statuses, tags, onTicketCreated }) => {
    const { currentUser } = useAuth();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [assigneeId, setAssigneeId] = useState<string | null>(null);
    const [sprintId, setSprintId] = useState<string | null>(null);
    const [statusId, setStatusId] = useState<number | null>(null);
    const [priority, setPriority] = useState<'Highest' | 'High' | 'Medium' | 'Low' | 'Lowest'>('Medium');
    const [endDate, setEndDate] = useState('');
    const [selectedTags, setSelectedTags] = useState<MultiValue<OptionType>>([]);
    const [errors, setErrors] = useState<{ title?: string }>({});
    const [files, setFiles] = useState<File[]>([]);
    const [isCreatingTicket, setIsCreatingTicket] = useState<boolean>(false);

    const assigneeOptions = assignees.map(a => ({ value: a.id, label: a.displayName }));
    const sprintOptions = sprints.map(s => ({ value: s.id, label: s.name }));
    const statusOptions = getSortedStatuses().map((s, index) => ({ value: (index + 1).toString(), label: s.name }));
    const tagOptions = tags.map(t => ({ value: t.id.toString(), label: t.name }));
    const priorityOptions = [
        { value: 'Highest', label: 'Highest' },
        { value: 'High', label: 'High' },
        { value: 'Medium', label: 'Medium' },
        { value: 'Low', label: 'Low' },
        { value: 'Lowest', label: 'Lowest' },
    ];

    const isBugReport = selectedTags.some(tag => tag.label.toLowerCase() === 'bug');

    const handleSubmit = async () => {
        if (!currentUser || isCreatingTicket) {
            return;
        }

        if (!title.trim()) {
            setErrors({ title: 'Title is required' });
            return;
        }
        setErrors({});

        setIsCreatingTicket(true);
        try {
            const token = await currentUser.getIdToken();

            if (isBugReport) {
                const bugReport: SubmitBugRequest = {
                    title,
                    description,
                    files,
                    appVersion: '1.0.0',
                    os: navigator.platform,
                    userAgent: navigator.userAgent,
                    viewport: `${window.innerWidth}x${window.innerHeight}`
                };
                const created = await submitBug(token, bugReport);
                if (created) {
                    onTicketCreated();
                    onHide();
                }
            } else {
                const selectedSprint = sprints.find(s => s.id === sprintId);

                const newTicket: NewTicketRequest = {
                    title,
                    reporterId: currentUser.uid,
                    startDate: new Date().toISOString(),
                    priority,
                };

                if (description) newTicket.description = description;
                if (assigneeId) newTicket.assigneeId = assigneeId;
                
                if (sprintId) {
                    newTicket.sprintId = selectedSprint?.name === 'Backlog' ? null : sprintId;
                } else {
                    newTicket.sprintId = null;
                }

                if (endDate) newTicket.endDate = new Date(endDate).toISOString();
                if (statusId) newTicket.statusId = statusId;
                if (selectedTags.length > 0) newTicket.tagIds = selectedTags.map(t => t.value);

                const created = await createTicket(token, newTicket);
                if (created) {
                    onTicketCreated();
                    onHide();
                }
            }
        } finally {
            setIsCreatingTicket(false);
        }
    };

    return (
        <Modal className='remove-input-txt-border' show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Create New Ticket</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={title} 
                            onChange={e => setTitle(e.target.value)} 
                            isInvalid={!!errors.title}
                            disabled={isCreatingTicket}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.title}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <RichTextEditor
                            value={description}
                            onChange={setDescription}
                            users={assignees}
                            placeholder="Add a description..."
                            readOnly={isCreatingTicket}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Assignee</Form.Label>
                        <Select options={assigneeOptions} onChange={opt => setAssigneeId(opt?.value || null)} isClearable isDisabled={isCreatingTicket} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Sprint</Form.Label>
                        <Select options={sprintOptions} onChange={opt => setSprintId(opt?.value || null)} isClearable isDisabled={isCreatingTicket} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Select options={statusOptions} onChange={opt => setStatusId(opt ? parseInt(opt.value) : null)} isClearable isDisabled={isCreatingTicket} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Priority</Form.Label>
                        <Select options={priorityOptions} defaultValue={priorityOptions[1]} onChange={opt => setPriority(opt?.value as 'Highest' | 'High' | 'Medium' | 'Low' | 'Lowest' || 'Medium')} isDisabled={isCreatingTicket} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Due Date</Form.Label>
                        <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={isCreatingTicket} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Tags</Form.Label>
                        <Select isMulti options={tagOptions} value={selectedTags} onChange={setSelectedTags} isDisabled={isCreatingTicket} />
                    </Form.Group>
                    {isBugReport && (
                        <Form.Group className="mb-3">
                            <Form.Label>Attachments</Form.Label>
                            <Form.Control 
                                type="file" 
                                multiple 
                                onChange={e => {
                                    const input = e.target as HTMLInputElement;
                                    if (input.files) {
                                        setFiles(Array.from(input.files));
                                    }
                                }} 
                                disabled={isCreatingTicket}
                            />
                            <div>
                                {files.map((file, i) => <div key={i}>{file.name}</div>)}
                            </div>
                        </Form.Group>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isCreatingTicket}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isCreatingTicket}>
                    {isCreatingTicket ? 'Creating...' : 'Create Ticket'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateTicketModal;